import { canAccess, hasEntity, type ActionAccess } from '@mefosa/shared'

/**
 * Where somebody lands after signing in, and where the catch-all route sends
 * them.
 *
 * It was `/dashboard` for everybody, which held while every role could read
 * one. The HR role cannot: it has no `report.operational`, deliberately —
 * that is the client reporting permission and HR has no business in the
 * pipeline. Signing in would have dropped them on a screen whose every request
 * 403s, which reads as a broken system rather than a deliberate boundary.
 *
 * Deriving it from what the person can actually open, rather than special-
 * casing the HR role, means the next role added does not reintroduce this.
 */
const CANDIDATES = [
  { path: '/dashboard', entity: 'dashboard', permission: 'report.operational' },
  { path: '/staff', entity: 'staff', permission: 'staff.read' },
  { path: '/today', entity: 'today', permission: 'followUp.read' },
  { path: '/companies', entity: 'companies', permission: 'company.read' },
  { path: '/invoices', entity: 'invoices', permission: 'invoice.read' },
] as const

export function landingPath(user: ActionAccess | null | undefined): string {
  // Signed out, the router sends them to /login anyway; /dashboard keeps the
  // existing behaviour for that moment rather than inventing a third answer.
  if (!user) return '/dashboard'

  for (const c of CANDIDATES) {
    if (canAccess(user, c.permission) && hasEntity(user, c.entity)) return c.path
  }

  /**
   * Nothing matched — an account narrowed to something not in the list above,
   * or a role yet to be given a landing screen.
   *
   * The dashboard is ALWAYS granted (DECISIONS.md §1) and is where the
   * catch-all redirects, so it is the only safe fallback: sending them to a
   * screen they lack would bounce them straight back here and loop.
   */
  return '/dashboard'
}
